import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../../utils/api';
import { isValidIpAddress } from './securityUtils';

const DEFAULT_PROTECTION_STATS = {
  fileProtection: { active: true, blocked: 0, effectiveness: 100 },
  botProtection: { active: true, blocked: 0, effectiveness: 100 },
  riskScoring: { active: true, highRiskIPs: 0, riskScore: 0 },
};

const DEFAULT_BLOCKED_IPS_PAGINATION = {
  page: 1,
  limit: 5,
  total: 0,
  totalPages: 1,
  hasNextPage: false,
  hasPrevPage: false,
};

export function useSecurityDashboard() {
  const { registerShellMeta, setRefreshHandler } = useOutletContext();
  const [dashboardData, setDashboardData] = useState(null);
  const [blockedIPsItems, setBlockedIPsItems] = useState([]);
  const [blockedIPsPagination, setBlockedIPsPagination] = useState(DEFAULT_BLOCKED_IPS_PAGINATION);
  const [healthData, setHealthData] = useState(null);
  const [diagnosticsData, setDiagnosticsData] = useState(null);
  const [blockedIPsLoading, setBlockedIPsLoading] = useState(true);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [eventSeverityFilter, setEventSeverityFilter] = useState('ALL');
  const [unblockConfirmIp, setUnblockConfirmIp] = useState(null);
  const [unblockBusy, setUnblockBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [protectionStats, setProtectionStats] = useState(DEFAULT_PROTECTION_STATS);
  const eventsSectionRef = useRef(null);
  const blockedIPsQueryRef = useRef({ page: 1, limit: 5, search: '' });
  const [blockedIPsRefreshKey, setBlockedIPsRefreshKey] = useState(0);

  const fetchBlockedIPs = useCallback(async (query = blockedIPsQueryRef.current) => {
    try {
      setBlockedIPsLoading(true);
      blockedIPsQueryRef.current = query;

      const response = await api.get('/api/security/blocked-ips', {
        params: {
          page: query.page,
          limit: query.limit,
          search: query.search || undefined,
        },
      });

      const data = response.data?.data || {};
      setBlockedIPsItems(data.items || []);
      setBlockedIPsPagination(data.pagination || DEFAULT_BLOCKED_IPS_PAGINATION);
    } catch (err) {
      console.error('Error fetching blocked IPs:', err);
      setBlockedIPsItems([]);
      setBlockedIPsPagination(DEFAULT_BLOCKED_IPS_PAGINATION);
    } finally {
      setBlockedIPsLoading(false);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      setRefreshing(true);
      setDiagnosticsLoading(true);

      const dashboardRes = await api.get('/api/security/dashboard');
      const result = dashboardRes.data;
      setDashboardData(result.data);
      setLastUpdatedAt(new Date().toISOString());

      const [healthRes, diagnosticsRes] = await Promise.allSettled([
        api.get('/api/security/health'),
        api.get('/api/security/diagnostics'),
      ]);

      if (healthRes.status === 'fulfilled') {
        setHealthData(healthRes.value.data?.data || null);
      }
      if (diagnosticsRes.status === 'fulfilled') {
        setDiagnosticsData(diagnosticsRes.value.data?.data || null);
      }

      setBlockedIPsRefreshKey((key) => key + 1);

      if (result.data.recentEvents) {
        const events = result.data.recentEvents;
        const fileBlocked =
          result.data.protectionStatus?.fileProtection?.blocked ||
          events.filter((e) => e.eventType === 'SENSITIVE_FILE_ACCESS').length;
        const botBlocked =
          result.data.protectionStatus?.botProtection?.blocked ||
          events.filter(
            (e) =>
              e.eventType === 'MALICIOUS_BOT_DETECTED' ||
              e.eventType === 'HIGH_RISK_BOT_BEHAVIOR' ||
              e.eventType === 'SCANNER_DETECTED'
          ).length;
        const highRiskIPs =
          result.data.protectionStatus?.threatIntelligence?.highRiskIPs ||
          result.data.suspiciousIPs?.filter((ip) => ip.count >= 5).length ||
          0;

        setProtectionStats({
          fileProtection: {
            active: result.data.protectionStatus?.fileProtection?.active ?? true,
            blocked: fileBlocked,
            effectiveness: result.data.protectionStatus?.fileProtection?.effectiveness ?? 100,
          },
          botProtection: {
            active: result.data.protectionStatus?.botProtection?.active ?? true,
            blocked: botBlocked,
            effectiveness: result.data.protectionStatus?.botProtection?.effectiveness ?? 100,
          },
          riskScoring: {
            active: result.data.protectionStatus?.threatIntelligence?.active ?? true,
            highRiskIPs,
            riskScore: result.data.protectionStatus?.threatIntelligence?.riskScore ?? 0,
          },
        });
      }
    } catch (err) {
      console.error('Error fetching security data:', err);
      const statusCode = err.response?.status;
      if (statusCode === 403) {
        setIsAuthenticated(false);
        setError('You do not have admin access to the security dashboard.');
      } else if (statusCode === 401) {
        setIsAuthenticated(false);
        setError('Your session has expired. Please sign in again.');
      } else if (!err.response) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (statusCode === 500) {
        setError('An unexpected error occurred. Please try again in a moment.');
      } else {
        setError('Failed to load security data. Please try again.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      setDiagnosticsLoading(false);
    }
    // No dependencies: this callback closes over nothing that changes between
    // renders. fetchBlockedIPs was listed but is never called here, which made
    // fetchDashboardData — and therefore the effect below — churn needlessly.
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    registerShellMeta({ lastUpdatedAt });
  }, [lastUpdatedAt, registerShellMeta]);

  useEffect(() => {
    setRefreshHandler(() => fetchDashboardData);
  }, [setRefreshHandler, fetchDashboardData]);

  const requestUnblockIP = useCallback((ipAddress) => {
    if (!isValidIpAddress(ipAddress)) {
      setActionMessage({ type: 'error', text: 'Invalid IP address format.' });
      return;
    }
    setUnblockConfirmIp(ipAddress);
  }, []);

  const cancelUnblockIP = useCallback(() => {
    if (!unblockBusy) setUnblockConfirmIp(null);
  }, [unblockBusy]);

  const confirmUnblockIP = useCallback(async () => {
    if (!unblockConfirmIp || unblockBusy) return;

    try {
      setUnblockBusy(true);
      await api.post('/api/security/unblock-ip', { ipAddress: unblockConfirmIp });
      setActionMessage({ type: 'success', text: `IP ${unblockConfirmIp} unblocked successfully.` });
      setUnblockConfirmIp(null);
      await fetchDashboardData();
    } catch (err) {
      console.error('Error unblocking IP:', err);
      const msg = err.response?.data?.error || 'Failed to unblock IP. Please try again.';
      setActionMessage({ type: 'error', text: msg });
    } finally {
      setUnblockBusy(false);
    }
  }, [unblockConfirmIp, unblockBusy, fetchDashboardData]);

  const reviewCriticalEvents = useCallback(() => {
    setEventSeverityFilter('CRITICAL');
    requestAnimationFrame(() => {
      eventsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const scrollToThreatAnalysis = useCallback(() => {
    document.getElementById('security-threat-analysis')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const filteredRecentEvents = useMemo(() => {
    const events = dashboardData?.recentEvents || [];
    const filtered =
      eventSeverityFilter === 'ALL'
        ? events
        : events.filter((event) => event.severity === eventSeverityFilter);
    return filtered.slice(0, 10);
  }, [dashboardData, eventSeverityFilter]);

  return {
    dashboardData,
    blockedIPsItems,
    blockedIPsPagination,
    blockedIPsLoading,
    fetchBlockedIPs,
    blockedIPsRefreshKey,
    healthData,
    diagnosticsData,
    diagnosticsLoading,
    lastUpdatedAt,
    protectionStats,
    loading,
    refreshing,
    error,
    isAuthenticated,
    actionMessage,
    setActionMessage,
    eventSeverityFilter,
    setEventSeverityFilter,
    filteredRecentEvents,
    eventsSectionRef,
    unblockConfirmIp,
    unblockBusy,
    fetchDashboardData,
    requestUnblockIP,
    cancelUnblockIP,
    confirmUnblockIP,
    reviewCriticalEvents,
    scrollToThreatAnalysis,
  };
}

export default useSecurityDashboard;
